import classNames from 'classnames';
import { Underlying } from 'models/underlying';

import './tag.scss';
import { type FC, type MouseEvent } from 'react';

type Variant = 'default' | 'restricted' | 'invalid';
export interface UnderlyingTagBaseProps {
  label: string;
  size?: 'lg' | 'md';

  isHighlighted?: boolean;
  isReadOnly?: boolean;
  onToggleHighlight?: () => void;
  onRemove?: () => void;
  className?: string;
  dataE2e?: string;
  dismissDataE2e?: string;
  variant?: Variant;
}

const VARIANT_BADGE_CLASS: Record<Variant, { default: string; highlighted: string }> = {
  default: { default: 'badge-default', highlighted: 'badge-success' },
  restricted: { default: 'badge-default', highlighted: 'badge-success' },
  invalid: {
    default: 'badge-outline-warning border border-warning',
    highlighted: 'badge-outline-success border border-success',
  },
};

const VARIANT_BUTTON_CLASS: Record<Variant, string> = {
  default: 'btn-flat-secondary',
  restricted: 'btn-flat-secondary',
  invalid: 'btn-flat-warning',
};

function getSpanClasses(variant: Variant, isHighlighted: boolean, isLarge: boolean, isReadOnly: boolean) {
  const badgeClass = VARIANT_BADGE_CLASS[variant][isHighlighted ? 'highlighted' : 'default'];
  const isRestricted = variant === 'restricted';
  return {
    [badgeClass]: true,
    'badge-lg': isLarge,
    'ps-0 pe-0': !isReadOnly && isRestricted,
    'ps-2 pe-0': isReadOnly && isRestricted && isLarge,
    'ps-0 pe-2': !isReadOnly && !isRestricted && isLarge,
    'ps-2 pe-2': isReadOnly && !isRestricted && isLarge,
    'ps-1 pe-0': isReadOnly && isRestricted && !isLarge,
    'ps-0 pe-1': !isReadOnly && !isRestricted && !isLarge,
    'ps-1 pe-1': isReadOnly && !isRestricted && !isLarge,
  };
}

export const UnderlyingTagBase: FC<UnderlyingTagBaseProps> = ({
  label,
  size = 'md',
  isHighlighted = false,
  onToggleHighlight,
  onRemove,
  className,
  dataE2e,
  dismissDataE2e,
  isReadOnly = false,
  variant = 'default',
}) => {
  const isLarge = size === 'lg';
  const spanClasses = getSpanClasses(variant, isHighlighted, isLarge, isReadOnly);
  const dismissButtonClasses = VARIANT_BUTTON_CLASS[variant];

  const handleRemove = (e: MouseEvent) => {
    e.stopPropagation();
    onRemove?.();
  };

  const handleToggleHighlight = (e: MouseEvent) => {
    e.stopPropagation();
    onToggleHighlight?.();
  };

  return (
    <span
      data-e2e={dataE2e}
      aria-label={label}
      onClick={handleToggleHighlight}
      className={classNames('base-underlying-badge badge badge-dismissible', spanClasses, className)}
    >
      {!isReadOnly && (
        <button
          type="button"
          data-e2e={dismissDataE2e}
          aria-label={`Remove ${label}`}
          className={classNames('btn btn-icon', dismissButtonClasses)}
          onClick={handleRemove}
        >
          <i className="icon" aria-hidden="true">
            close
          </i>
        </button>
      )}
      {label}
      {variant === 'restricted' && (
        <span className="btn btn-icon">
          <i className="icon icon-outlined text-warning" aria-hidden="true">
            lock
          </i>
        </span>
      )}
    </span>
  );
};

export type UnderlyingTagProps = Omit<UnderlyingTagBaseProps, 'variant'> & {
  value?: Underlying;
};

const getVariantByUnderlying = (underlying?: Underlying): UnderlyingTagBaseProps['variant'] => {
  if (!underlying || underlying.hasMissingCodes) {
    return 'invalid';
  }

  if (underlying.isRestricted) {
    return 'restricted';
  }

  return 'default';
};

export const UnderlyingTag: FC<UnderlyingTagProps> = ({ value, ...rest }) => {
  const variant = getVariantByUnderlying(value);
  return <UnderlyingTagBase {...rest} variant={variant} />;
};
